import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';
import { TrendingUp, Users, Coffee, Activity } from 'lucide-react';
const chartData = [{
  year: '2019',
  production: 100
}, {
  year: '2020',
  production: 115
}, {
  year: '2021',
  production: 110
}, {
  year: '2022',
  production: 130
}, {
  year: '2023',
  production: 145
}, {
  year: '2024',
  production: 160
}];
// Simple animated counter component
const AnimatedCounter = ({
  end,
  suffix = '',
  duration = 2
}: {
  end: number;
  suffix?: string;
  duration?: number;
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);
      return () => clearInterval(timer);
    }
  }, [end, duration, isInView]);
  return <span ref={ref} data-id="element-425">
      {count}
      {suffix}
    </span>;
};
export function StatsSection() {
  return <section className="py-16 bg-white border-y border-gray-100" data-id="element-426">
      <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-427">
        <div className="flex flex-col lg:flex-row gap-12 items-center" data-id="element-428">
          {/* Left: Text & Chart */}
          <div className="w-full lg:w-1/2" data-id="element-429">
            <h2 className="text-3xl font-bold text-uganda-black mb-4" data-id="element-430">
              Sector Performance
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-431">
              The agricultural sector remains the backbone of Uganda's economy,
              driving export growth, ensuring food security, and providing
              employment for the majority of the population.
            </p>

            <div className="bg-uganda-light-green rounded-xl p-6 border border-green-100" data-id="element-432">
              <div className="flex justify-between items-end mb-4" data-id="element-433">
                <div data-id="element-434">
                  <h4 className="text-sm font-semibold text-uganda-green uppercase tracking-wider" data-id="element-435">
                    Food Production Index
                  </h4>
                  <div className="text-2xl font-bold text-uganda-black mt-1" data-id="element-436">
                    160.0 pts
                  </div>
                </div>
                <div className="flex items-center text-uganda-green text-sm font-medium" data-id="element-437">
                  <TrendingUp className="w-4 h-4 mr-1" data-id="element-438" /> +10.3%
                </div>
              </div>
              <div className="h-48 w-full" data-id="element-439">
                <ResponsiveContainer width="100%" height="100%" data-id="element-440">
                  <AreaChart data={chartData} margin={{
                  top: 5,
                  right: 0,
                  left: 0,
                  bottom: 0
                }} data-id="element-441">
                    <defs data-id="element-442">
                      <linearGradient id="colorProd" x1="0" y1="0" x2="0" y2="1" data-id="element-443">
                        <stop offset="5%" stopColor="#1a6b3c" stopOpacity={0.3} data-id="element-444" />
                        <stop offset="95%" stopColor="#1a6b3c" stopOpacity={0} data-id="element-445" />
                      </linearGradient>
                    </defs>
                    <Tooltip contentStyle={{
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }} labelStyle={{
                    color: '#6b7280',
                    marginBottom: '4px'
                  }} data-id="element-446" />
                    <Area type="monotone" dataKey="production" stroke="#1a6b3c" strokeWidth={3} fillOpacity={1} fill="url(#colorProd)" data-id="element-447" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Right: Stats Grid */}
          <div className="w-full lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-448">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-449">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-uganda-green mb-4" data-id="element-450">
                <Activity className="w-5 h-5" data-id="element-451" />
              </div>
              <div className="text-3xl font-bold text-uganda-black mb-1" data-id="element-452">
                <AnimatedCounter end={24} suffix="%" data-id="element-453" />
              </div>
              <p className="text-sm text-gray-500 font-medium" data-id="element-454">
                Agricultural GDP Contribution
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-455">
              <div className="w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center text-uganda-yellow mb-4" data-id="element-456">
                <Coffee className="w-5 h-5" data-id="element-457" />
              </div>
              <div className="text-3xl font-bold text-uganda-black mb-1" data-id="element-458">
                <AnimatedCounter end={6.5} suffix="M" data-id="element-459" />
              </div>
              <p className="text-sm text-gray-500 font-medium" data-id="element-460">
                Bags of Coffee Exported (2023)
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-461">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 mb-4" data-id="element-462">
                <Users className="w-5 h-5" data-id="element-463" />
              </div>
              <div className="text-3xl font-bold text-uganda-black mb-1" data-id="element-464">
                <AnimatedCounter end={1.2} suffix="M+" data-id="element-465" />
              </div>
              <p className="text-sm text-gray-500 font-medium" data-id="element-466">
                Registered Farmers
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-467">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-uganda-red mb-4" data-id="element-468">
                <TrendingUp className="w-5 h-5" data-id="element-469" />
              </div>
              <div className="text-3xl font-bold text-uganda-black mb-1" data-id="element-470">
                <AnimatedCounter end={45} data-id="element-471" />
              </div>
              <p className="text-sm text-gray-500 font-medium" data-id="element-472">
                Active National Programs
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}